/**
  Fibre-G Precision Nutrition - Storefront Authentication & User Session Manager
  - Integrates Shopify New Customer Accounts for native customer portal.
  - Customer Verification Modal: Collects Full Name, Mobile (delivery only), and Email.
  - Email OTP is the primary verification method for order checkout. Mobile OTP is never used.
 */
window.FibregAuth = (function () {
  'use strict';

  let activeChallengeId = null;
  let pendingCustomer = null;
  let onVerifiedCallback = null;
  let cooldownTimerInterval = null;

  function getApiUrl() {
    if (window.location.protocol === 'https:' && !window.location.hostname.includes('localhost') && !window.location.hostname.includes('127.0.0.1')) {
      if (window.FibregTheme && window.FibregTheme.apiUrl && window.FibregTheme.apiUrl.startsWith('https:')) {
        return window.FibregTheme.apiUrl;
      }
      return 'https://fibre-g-server-production.up.railway.app';
    }

    if (
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === 'localhost' ||
      window.location.port === '9292'
    ) {
      return 'http://localhost:3001';
    }
    if (window.FibregTheme && window.FibregTheme.apiUrl) {
      return window.FibregTheme.apiUrl;
    }
    return 'https://fibre-g-server-production.up.railway.app';
  }

  // Robust API Fetch with automatic live production failover if local server is down
  async function apiFetch(path, options) {
    const primary = getApiUrl();
    try {
      const res = await fetch(`${primary}${path}`, options);
      return res;
    } catch (err) {
      if (!primary.includes('railway.app')) {
        console.warn('[FibregAuth] Local API error, retrying with live server:', err);
        const fallback = 'https://fibre-g-server-production.up.railway.app';
        return await fetch(`${fallback}${path}`, options);
      }
      throw err;
    }
  }

  function getToken() {
    try {
      return localStorage.getItem('fibreg_auth_token') || getVerifiedCustomer()?.token || null;
    } catch (e) {
      return null;
    }
  }

  function getUser() {
    if (window.FibregShopifyCustomer && window.FibregShopifyCustomer.email) {
      return {
        id: window.FibregShopifyCustomer.id,
        email: window.FibregShopifyCustomer.email,
        name: window.FibregShopifyCustomer.name || window.FibregShopifyCustomer.firstName || 'Customer',
        phone: window.FibregShopifyCustomer.phone || '',
      };
    }
    const verified = getVerifiedCustomer();
    if (verified && verified.email) {
      return {
        id: verified.token || 'verified_user',
        email: verified.email,
        name: verified.name || 'Valued Customer',
        phone: verified.mobile || '',
      };
    }
    return null;
  }

  function isAuthenticated() {
    return !!(getUser()?.email);
  }

  function getVerifiedCustomer() {
    try {
      const stored = sessionStorage.getItem('fibreg_verified_customer');
      if (stored) return JSON.parse(stored);
      const local = localStorage.getItem('fibreg_auth_user');
      if (local) return JSON.parse(local);
    } catch (e) {}
    return null;
  }

  function isEmailVerified() {
    return isAuthenticated();
  }

  function setVerifiedCustomer(customerData) {
    try {
      sessionStorage.setItem('fibreg_verified_customer', JSON.stringify(customerData));
      if (customerData.token) {
        localStorage.setItem('fibreg_auth_token', customerData.token);
      }
      localStorage.setItem('fibreg_auth_user', JSON.stringify(customerData));
    } catch (e) {}
    window.dispatchEvent(
      new CustomEvent('fibreg:verified:change', {
        detail: { customer: customerData },
      })
    );
    notifyAuthChange();
    updateUI();
  }

  function setAuth(_token, _user) {
    notifyAuthChange();
  }

  function logout() {
    try {
      sessionStorage.removeItem('fibreg_verified_customer');
      sessionStorage.removeItem('fibreg_auth_redirect');
      localStorage.removeItem('fibreg_auth_token');
      localStorage.removeItem('fibreg_auth_user');
    } catch (e) {}

    notifyAuthChange();

    const logoutUrl = window.FibregTheme?.routes?.account_logout_url || '/account/logout';
    if (window.top && window.top !== window) {
      window.top.location.href = logoutUrl;
    } else {
      window.location.href = logoutUrl;
    }
  }

  function notifyAuthChange() {
    const isAuthed = isAuthenticated();
    const user = getUser();
    window.dispatchEvent(
      new CustomEvent('fibreg:auth:change', {
        detail: {
          isAuthenticated: isAuthed,
          user: user,
        },
      })
    );
    updateUI();
  }

  // Opens Fibre-G branded email OTP verification modal
  function openLoginModal(e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }
    return openVerificationModal();
  }

  function closeLoginModal() {
    closeVerificationModal();
  }

  /* =========================================================================
   * EMAIL OTP VERIFICATION MODAL CONTROLLER
   * ========================================================================= */

  function getModalEls() {
    return {
      modal: document.getElementById('fibreg-auth-modal'),
      card: document.getElementById('fibreg-auth-modal-card'),
      closeBtn: document.getElementById('fibreg-auth-close-btn'),
      alert: document.getElementById('fibreg-auth-alert'),
      stepDetails: document.getElementById('fibreg-auth-step-details'),
      stepOtp: document.getElementById('fibreg-auth-step-otp'),
      stepSuccess: document.getElementById('fibreg-auth-step-success'),
      inputName: document.getElementById('fibreg-auth-input-name'),
      inputMobile: document.getElementById('fibreg-auth-input-mobile'),
      inputEmail: document.getElementById('fibreg-auth-input-email'),
      sendBtn: document.getElementById('fibreg-auth-submit-send-btn'),
      sendBtnText: document.getElementById('fibreg-auth-send-btn-text'),
      sendSpinner: document.getElementById('fibreg-auth-send-spinner'),
      displayEmail: document.getElementById('fibreg-auth-display-email'),
      inputCode: document.getElementById('fibreg-auth-input-code'),
      verifyBtn: document.getElementById('fibreg-auth-submit-verify-btn'),
      verifyBtnText: document.getElementById('fibreg-auth-verify-btn-text'),
      verifySpinner: document.getElementById('fibreg-auth-verify-spinner'),
      btnBack: document.getElementById('fibreg-auth-btn-back'),
      btnResend: document.getElementById('fibreg-auth-btn-resend'),
    };
  }

  function showAlert(msg, isSuccess) {
    const { alert } = getModalEls();
    if (!alert) return;
    alert.textContent = msg;
    alert.style.display = 'block';
    if (isSuccess) {
      alert.style.background = '#f0fdf4';
      alert.style.color = '#166534';
      alert.style.border = '1px solid #bbf7d0';
    } else {
      alert.style.background = '#fef2f2';
      alert.style.color = '#991b1b';
      alert.style.border = '1px solid #fecaca';
    }
  }

  function hideAlert() {
    const { alert } = getModalEls();
    if (!alert) return;
    alert.style.display = 'none';
  }

  function showStep(stepName) {
    const els = getModalEls();
    if (els.stepDetails) {
      if (stepName === 'details') {
        els.stepDetails.classList.remove('hidden');
        els.stepDetails.style.display = 'block';
      } else {
        els.stepDetails.classList.add('hidden');
        els.stepDetails.style.display = 'none';
      }
    }
    if (els.stepOtp) {
      if (stepName === 'otp') {
        els.stepOtp.classList.remove('hidden');
        els.stepOtp.style.display = 'block';
      } else {
        els.stepOtp.classList.add('hidden');
        els.stepOtp.style.display = 'none';
      }
    }
    if (els.stepSuccess) {
      if (stepName === 'success') {
        els.stepSuccess.classList.remove('hidden');
        els.stepSuccess.style.display = 'block';
      } else {
        els.stepSuccess.classList.add('hidden');
        els.stepSuccess.style.display = 'none';
      }
    }
  }

  function openVerificationModal(options) {
    const els = getModalEls();
    if (!els.modal) return;

    onVerifiedCallback = (options && typeof options.onVerified === 'function') ? options.onVerified : null;

    hideAlert();
    showStep('details');

    const existing = getUser();
    if (existing) {
      if (els.inputName && !els.inputName.value) els.inputName.value = existing.name || '';
      if (els.inputMobile && !els.inputMobile.value) els.inputMobile.value = existing.phone || '';
      if (els.inputEmail && !els.inputEmail.value) els.inputEmail.value = existing.email || '';
    }

    els.modal.classList.remove('hidden');
    els.modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      if (els.inputName && !els.inputName.value) {
        els.inputName.focus();
      } else if (els.inputEmail) {
        els.inputEmail.focus();
      }
    }, 100);
  }

  function closeVerificationModal() {
    const els = getModalEls();
    if (!els.modal) return;
    els.modal.classList.add('hidden');
    els.modal.style.display = 'none';
    document.body.style.overflow = '';
    hideAlert();
    if (cooldownTimerInterval) {
      clearInterval(cooldownTimerInterval);
      cooldownTimerInterval = null;
    }
  }

  // Step 1: Send Email Verification Code
  async function handleSendEmailOtp() {
    const els = getModalEls();
    hideAlert();

    const name = els.inputName.value.trim() || (pendingCustomer && pendingCustomer.name) || '';
    const mobile = els.inputMobile.value.trim() || (pendingCustomer && pendingCustomer.mobile) || '';
    const email = els.inputEmail.value.trim() || (pendingCustomer && pendingCustomer.email) || '';

    if (!name) {
      showAlert('Please enter your full name.', false);
      els.inputName.focus();
      return;
    }
    if (!mobile || mobile.replace(/\D/g, '').length < 7) {
      showAlert('Please enter a valid mobile number for order delivery.', false);
      els.inputMobile.focus();
      return;
    }
    if (!email || !email.includes('@') || !email.includes('.')) {
      showAlert('Please enter a valid email address.', false);
      els.inputEmail.focus();
      return;
    }

    // Set loading state
    els.sendBtn.disabled = true;
    els.sendBtnText.textContent = 'Sending Code...';
    els.sendSpinner.classList.remove('hidden');
    if (els.btnResend) {
      els.btnResend.textContent = 'Sending...';
    }

    try {
      const res = await apiFetch('/api/auth/email-otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, mobile, email }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || data.error || 'Failed to send verification code.');
      }

      activeChallengeId = data.challengeId;
      pendingCustomer = { name, mobile, email };

      // Transition to Step 2: OTP Entry
      els.displayEmail.textContent = data.maskedEmail || email;
      showStep('otp');
      els.inputCode.value = '';

      showAlert('Verification code sent! Please check your email inbox.', true);

      startCooldownTimer(data.cooldownSeconds || 60);

      setTimeout(() => els.inputCode.focus(), 150);
    } catch (err) {
      showAlert(err.message, false);
      if (els.btnResend && !cooldownTimerInterval) {
        els.btnResend.disabled = false;
        els.btnResend.textContent = 'Resend Code';
      }
    } finally {
      els.sendBtn.disabled = false;
      els.sendBtnText.textContent = 'Send Verification Code';
      els.sendSpinner.classList.add('hidden');
      if (els.btnResend && !cooldownTimerInterval) {
        els.btnResend.textContent = 'Resend Code';
      }
    }
  }

  // Step 2: Verify Email OTP Code
  async function handleVerifyEmailOtp() {
    const els = getModalEls();
    hideAlert();

    const code = els.inputCode.value.trim().replace(/\D/g, '');

    if (!code || code.length !== 6) {
      showAlert('Please enter the 6-digit code received by email.', false);
      els.inputCode.focus();
      return;
    }

    if (!activeChallengeId) {
      showAlert('Session expired. Please request a new code.', false);
      handleBackToDetails();
      return;
    }

    els.verifyBtn.disabled = true;
    els.verifyBtnText.textContent = 'Verifying...';
    els.verifySpinner.classList.remove('hidden');

    try {
      const res = await apiFetch('/api/auth/email-otp/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeId: activeChallengeId, code }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Verification failed. Please try again.');
      }

      // Success: Save verified customer state
      const verifiedPayload = {
        name: pendingCustomer.name,
        mobile: pendingCustomer.mobile,
        email: pendingCustomer.email,
        token: data.verificationToken,
        verifiedAt: data.customer?.verifiedAt || new Date().toISOString(),
      };

      setVerifiedCustomer(verifiedPayload);

      // Transition to Step 3: Success
      showStep('success');

      setTimeout(() => {
        closeVerificationModal();
        if (typeof onVerifiedCallback === 'function') {
          const cb = onVerifiedCallback;
          onVerifiedCallback = null;
          cb(verifiedPayload);
        }
      }, 900);
    } catch (err) {
      showAlert(err.message, false);
      els.inputCode.select();
    } finally {
      els.verifyBtn.disabled = false;
      els.verifyBtnText.textContent = 'Verify & Continue';
      els.verifySpinner.classList.add('hidden');
    }
  }

  function handleBackToDetails() {
    hideAlert();
    showStep('details');
  }

  function startCooldownTimer(seconds) {
    const els = getModalEls();
    if (!els.btnResend) return;

    if (cooldownTimerInterval) clearInterval(cooldownTimerInterval);

    let remaining = seconds;
    els.btnResend.disabled = true;
    els.btnResend.textContent = `Resend in ${remaining}s`;

    cooldownTimerInterval = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(cooldownTimerInterval);
        cooldownTimerInterval = null;
        els.btnResend.disabled = false;
        els.btnResend.textContent = 'Resend Code';
      } else {
        els.btnResend.textContent = `Resend in ${remaining}s`;
      }
    }, 1000);
  }

  function checkAuth() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('auth_test')) {
        params.delete('auth_test');
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
    } catch (e) {}

    updateUI();
    return getUser();
  }

  async function saveProfileName(_name) {
    return null;
  }

  async function saveProtocolSubmission(_input, _id) {
    return null;
  }

  async function getUserHistory() {
    return [];
  }

  function updateUI() {
    const isAuthed = isAuthenticated() || isEmailVerified();
    const user = getUser();

    const loginButtons = document.querySelectorAll('[data-auth-login-btn], [data-auth-login-link], [data-action-login]');
    const profileControls = document.querySelectorAll('[data-auth-profile-control]');
    const customerNameEls = document.querySelectorAll('[data-customer-name]');

    loginButtons.forEach(function (btn) {
      if (isAuthed) {
        btn.classList.add('hidden');
        btn.style.display = 'none';
      } else {
        btn.classList.remove('hidden');
        btn.style.display = btn.tagName === 'DIV' ? 'block' : 'inline-flex';
      }
    });

    profileControls.forEach(function (ctrl) {
      if (isAuthed) {
        ctrl.classList.remove('hidden');
        ctrl.style.display = ctrl.classList.contains('flex-col') ? 'flex' : 'inline-flex';
      } else {
        ctrl.classList.add('hidden');
        ctrl.style.display = 'none';
      }
    });

    const cartControls = document.querySelectorAll('[data-cart-trigger], [data-auth-cart-control]');
    cartControls.forEach(function (cartBtn) {
      if (isAuthed) {
        cartBtn.classList.remove('hidden');
        cartBtn.style.display = 'inline-flex';
      } else {
        cartBtn.classList.add('hidden');
        cartBtn.style.display = 'none';
      }
    });

    customerNameEls.forEach(function (el) {
      const displayName = user?.name || user?.email || (user?.phone && !user.phone.startsWith('+email_') ? user.phone : 'My Account');
      el.textContent = displayName;
    });

    const historyLinks = document.querySelectorAll('[data-auth-history-link]');
    historyLinks.forEach(function (link) {
      if (isAuthed) {
        link.classList.remove('hidden');
        link.style.display = 'inline-flex';
      } else {
        link.classList.add('hidden');
        link.style.display = 'none';
      }
    });
  }

  // Customer Profile & Order Services (Connected to same-origin Shopify App Proxy)
  async function getCustomerProfile() {
    if (!isAuthenticated()) return getUser();
    try {
      const res = await fetch('/apps/fibre-g-api/customer/profile', {
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        return data.user || getUser();
      }
    } catch (e) {
      console.warn('[FibregAuth] App Proxy profile request fallback:', e);
    }
    return getUser();
  }

  async function updateCustomerProfile(payload) {
    if (!isAuthenticated()) return null;
    try {
      const res = await fetch('/apps/fibre-g-api/customer/profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload || {}),
      });
      if (res.ok) {
        const data = await res.json();
        return data.user || null;
      }
    } catch (e) {
      console.warn('[FibregAuth] App Proxy profile update fallback:', e);
    }
    return null;
  }

  async function getCustomerOrders() {
    if (!isAuthenticated()) return { orders: [], total: 0 };
    try {
      const res = await fetch('/apps/fibre-g-api/customer/orders', {
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        return data;
      }
    } catch (e) {
      console.warn('[FibregAuth] App Proxy orders request fallback:', e);
    }
    return { orders: [], total: 0 };
  }

  async function getCustomerOrderDetails(orderId) {
    if (!isAuthenticated() || !orderId) return null;
    try {
      const res = await fetch('/apps/fibre-g-api/customer/orders/' + encodeURIComponent(orderId), {
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        return data.order || null;
      }
    } catch (e) {
      console.warn('[FibregAuth] App Proxy order detail request fallback:', e);
    }
    return null;
  }

  function init() {
    // Event listeners for modal elements
    const els = getModalEls();

    if (els.sendBtn) {
      els.sendBtn.addEventListener('click', handleSendEmailOtp);
    }
    if (els.verifyBtn) {
      els.verifyBtn.addEventListener('click', handleVerifyEmailOtp);
    }
    if (els.btnBack) {
      els.btnBack.addEventListener('click', handleBackToDetails);
    }
    if (els.btnResend) {
      els.btnResend.addEventListener('click', handleSendEmailOtp);
    }
    if (els.closeBtn) {
      els.closeBtn.addEventListener('click', closeVerificationModal);
    }
    if (els.modal) {
      els.modal.addEventListener('click', function (e) {
        if (e.target === els.modal) closeVerificationModal();
      });
    }

    // Auto-advance or submit on 6 digits
    if (els.inputCode) {
      els.inputCode.addEventListener('input', function () {
        this.value = this.value.replace(/\D/g, '').slice(0, 6);
        if (this.value.length === 6) {
          handleVerifyEmailOtp();
        }
      });
    }

    // Clean, direct listeners for login / logout triggers
    document.addEventListener('click', function (e) {
      // Verification modal trigger (e.g. checkout gate or verification button)
      const verifyTrigger = e.target.closest('[data-action-verify], [data-auth-verify-btn]');
      if (verifyTrigger) {
        e.preventDefault();
        openVerificationModal();
        return;
      }

      // Account / Profile trigger (intercepts all account links)
      const accountTrigger = e.target.closest(
        'a[href="/account"], a[href*="/account/"], a[href="/pages/profile"], [data-auth-profile-control], [data-action-account]'
      );
      if (accountTrigger) {
        if (isAuthenticated()) {
          if (window.location.pathname !== '/pages/profile') {
            e.preventDefault();
            e.stopPropagation();
            window.location.href = '/pages/profile';
          }
          return;
        } else {
          e.preventDefault();
          e.stopPropagation();
          openVerificationModal({
            onVerified: function () {
              window.location.href = '/pages/profile';
            },
          });
          return;
        }
      }

      // Login trigger - always open Fibre-G themed popup modal
      const loginTrigger = e.target.closest(
        '[data-action-login], [data-auth-login-btn], [data-auth-login-link], a[href*="/account/login"], a[href*="customer_authentication"]'
      );
      if (loginTrigger) {
        e.preventDefault();
        e.stopPropagation();
        if (isAuthenticated()) {
          window.location.href = '/pages/profile';
          return;
        }
        openVerificationModal();
        return;
      }

      const logoutTrigger = e.target.closest('[data-action-logout]');
      if (logoutTrigger) {
        e.preventDefault();
        logout();
      }
    });

    checkAuth();
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
  document.addEventListener('shopify:section:load', init);

  return {
    getToken: getToken,
    getUser: getUser,
    isAuthenticated: isAuthenticated,
    getVerifiedCustomer: getVerifiedCustomer,
    isEmailVerified: isEmailVerified,
    setVerifiedCustomer: setVerifiedCustomer,
    setAuth: setAuth,
    logout: logout,
    openLoginModal: openLoginModal,
    closeLoginModal: closeLoginModal,
    openVerificationModal: openVerificationModal,
    closeVerificationModal: closeVerificationModal,
    checkAuth: checkAuth,
    saveProfileName: saveProfileName,
    saveProtocolSubmission: saveProtocolSubmission,
    getUserHistory: getUserHistory,
    getCustomerProfile: getCustomerProfile,
    updateCustomerProfile: updateCustomerProfile,
    getCustomerOrders: getCustomerOrders,
    getCustomerOrderDetails: getCustomerOrderDetails,
    updateUI: updateUI,
    getApiUrl: getApiUrl,
  };
})();
